/**
 * QuickActionsPanel Component
 *
 * Panel displaying common quick action buttons for dashboard pages.
 * Provides easy access to frequently used features.
 *
 * Usage:
 * <QuickActionsPanel
 *   actions={[
 *     {
 *       id: 'new-app',
 *       label: 'New Application',
 *       icon: <Plus className="w-5 h-5" />,
 *       onClick: () => navigate('/applications/new'),
 *       variant: 'primary'
 *     }
 *   ]}
 * />
 */

import type { ReactElement } from 'react';
import { Button } from '../common/Button';

export interface QuickAction {
  id: string;
  label: string;
  icon?: ReactElement;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  disabled?: boolean;
}

export interface QuickActionsPanelProps {
  /** Quick action items */
  actions: QuickAction[];

  /** Panel title */
  title?: string;

  /** Layout orientation */
  layout?: 'horizontal' | 'vertical' | 'grid';

  /** Custom className */
  className?: string;
}

export function QuickActionsPanel({
  actions,
  title,
  layout = 'horizontal',
  className = '',
}: QuickActionsPanelProps) {
  const layoutClasses = {
    horizontal: 'flex flex-wrap gap-3',
    vertical: 'flex flex-col gap-2',
    grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3',
  };

  if (actions.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {title && (
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">{title}</h3>
      )}

      <div className={layoutClasses[layout]}>
        {actions.map((action) => (
          <Button
            key={action.id}
            variant={action.variant || 'outline'}
            icon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled}
            className={layout === 'vertical' || layout === 'grid' ? 'w-full justify-start' : ''}
          >
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export default QuickActionsPanel;
